import Highcharts from 'highcharts';
import { SM_BREAK } from '../RetirementChart.meta';

const VERTICAL_OFFSET = 40;
const HORIZONTAL_OFFSET = 10;

// Per-chart state so that multiple RetirementChart/ScenarioProjection instances
// mounted in the same document (e.g. multi-section PDF) don't clobber each
// other's annotations via shared module-level maps.
const annotationsByChart = new WeakMap<
  Highcharts.Chart,
  Record<string, Highcharts.SVGElement>
>();

const getAnnotations = (chart: Highcharts.Chart) => {
  let annotations = annotationsByChart.get(chart);
  if (!annotations) {
    annotations = {};
    annotationsByChart.set(chart, annotations);
  }
  return annotations;
};

interface RenderAnnotationOpts {
  chart: Highcharts.Chart;
  point: Highcharts.Point & { plotX?: number; plotY?: number }; // https://github.com/highcharts/highcharts/issues/13738
  id: string;
  label: string;
  className: string;
}

export const renderAnnotation = ({ chart, point, id, label, className }: RenderAnnotationOpts) => {
  const annotations = getAnnotations(chart);

  if (chart.chartWidth < SM_BREAK) {
    Object.values(annotations).forEach(annotation => annotation.destroy());
    annotationsByChart.set(chart, {});

    return;
  }

  if (annotations[id]) {
    annotations[id].destroy();
  }

  const leftAligned = {
    x: (point.plotX || 0) + chart.plotLeft + HORIZONTAL_OFFSET,
    y: (point.plotY || 0) + chart.plotTop - VERTICAL_OFFSET,
    align: 'left',
  };
  const rightAligned = {
    x: (point.plotX || 0) + chart.plotLeft - HORIZONTAL_OFFSET,
    y: (point.plotY || 0) + chart.plotTop - VERTICAL_OFFSET,
    align: 'right',
  };
  const middleAligned = {
    x: (point.plotX || 0) + chart.plotLeft - HORIZONTAL_OFFSET,
    y: (point.plotY || 0) + chart.plotTop,
    align: 'center',
  };
  const topAligned = {
    x: (point.plotX || 0) + chart.plotLeft,
    y: (point.plotY || 0) + chart.plotTop - VERTICAL_OFFSET,
    align: 'center',
  };

  annotations[id] = chart.renderer
    .g(id)
    .addClass(className)
    .attr({ zIndex: 3 })
    .add();

  // Default position
  let text = chart.renderer
    .text(label, topAligned.x, topAligned.y, false)
    .attr({
      align: topAligned.align,
    })
    .add(annotations[id]);

  // Annotation overflowing on the left side of the chart
  if (text.getBBox().x < chart.plotLeft) {
    text.destroy();
    text = chart.renderer
      .text(label, leftAligned.x, leftAligned.y, false)
      .attr({
        align: leftAligned.align,
      })
      .add(annotations[id]);
  }

  // Annotation overflowing on the right side of the chart
  if (text.getBBox().x + text.getBBox().width > chart.plotWidth) {
    text.destroy();
    text = chart.renderer
      .text(label, rightAligned.x, rightAligned.y, false)
      .attr({
        align: rightAligned.align,
      })
      .add(annotations[id]);
  }

  // Annotation too high, show it on the right side of the marker
  if (text.getBBox().y < 0) {
    const cachedTextWidth = text.getBBox().width;
    text.destroy();
    text = chart.renderer
      .text(label, middleAligned.x - cachedTextWidth / 2, middleAligned.y, false)
      .attr({
        align: middleAligned.align,
      })
      .add(annotations[id]);
  }
};

export const removeAnnotation = (chart: Highcharts.Chart, id: string) => {
  const annotations = annotationsByChart.get(chart);
  if (annotations?.[id]) {
    annotations[id].destroy();

    delete annotations[id];
  }
};
